import {
    Box,
    Button,
    Chip,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import SaveIcon from "@material-ui/icons/Save"
import React, { useEffect, useState } from "react"
import {
    alternarIntegrador,
    criarIntegrador,
    editarIntegrador,
    Integrador,
    listarIntegradores,
} from "../../../services/openDelivery"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { useSnackbar } from "../../../utils/notificacoes"

/**
 * Tipo do parceiro. O backend só conhece o booleano `logistica`, então PDV e Cardápio Digital
 * viajam iguais para a API — a distinção entre eles ainda não existe do outro lado.
 */
const TIPOS_PDV = [
    {
        valor: "PDV",
        rotulo: "PDV",
        descricao:
            "Consome a nossa API e recebe credenciais geradas por nós — a chamada parte dele.",
        disponivel: true,
    },
    {
        valor: "LOGISTICA",
        rotulo: "Logística",
        descricao:
            "Praça de entrega: nós é que chamamos o parceiro usando as credenciais dele — a direção é oposta.",
        disponivel: true,
    },
    {
        valor: "CARDAPIO_DIGITAL",
        rotulo: "Cardápio Digital (ainda não implementado)",
        descricao: "",
        disponivel: false,
    },
] as const

type TipoPdv = (typeof TIPOS_PDV)[number]["valor"]

const formularioVazio = {
    nome: "",
    contatoEmail: "",
    site: "",
    webhookUrl: "",
    appIdParceiro: "",
    observacao: "",
    tipoPdv: "PDV" as TipoPdv,
}

/**
 * Cadastro dos parceiros homologados a usar a nossa API Open Delivery.
 *
 * Restrito à equipe Cliente Fiel: é o lojista quem conecta a loja dele a um parceiro, mas quem
 * decide QUAIS parceiros existem somos nós. Sem isso, qualquer loja poderia liberar acesso aos
 * próprios pedidos para um sistema não homologado.
 */
export function OpenDeliveryIntegradoresForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [integradores, setIntegradores] = useState<Integrador[]>([])
    const [formulario, setFormulario] = useState(formularioVazio)
    /** Integrador em edição; null = o formulário está cadastrando um novo. */
    const [emEdicao, setEmEdicao] = useState<Integrador | null>(null)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant: tipo })

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await listarIntegradores(autorizacao)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao carregar", "error")
            else setIntegradores(res)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChange =
        (campo: keyof typeof formularioVazio) =>
        (e: React.ChangeEvent<{ value: unknown }>) => {
            // Lê o valor ANTES do updater: o React 16 recicla o evento sintético, então
            // e.target já seria null dentro da função passada ao setFormulario.
            const valor = e.target.value as string
            setFormulario(prev => ({ ...prev, [campo]: valor }))
        }

    const handleEditar = (integrador: Integrador) => {
        setEmEdicao(integrador)
        setFormulario({
            nome: integrador.nome ?? "",
            contatoEmail: integrador.contatoEmail ?? "",
            site: integrador.site ?? "",
            webhookUrl: integrador.webhookUrl ?? "",
            appIdParceiro: integrador.appIdParceiro ?? "",
            observacao: integrador.observacao ?? "",
            tipoPdv: integrador.logistica ? "LOGISTICA" : "PDV",
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const handleCancelarEdicao = () => {
        setEmEdicao(null)
        setFormulario(formularioVazio)
    }

    const handleSalvar = async () => {
        if (!formulario.nome.trim()) {
            alertStart("O campo Nome é obrigatório.", "error")
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        try {
            const { tipoPdv, ...campos } = formulario
            const res = emEdicao
                ? await editarIntegrador(autorizacao, emEdicao.id, campos)
                : await criarIntegrador(autorizacao, {
                      ...campos,
                      logistica: tipoPdv === "LOGISTICA",
                  })
            if ("retornoErro" in res) {
                alertStart(
                    res.mensagem ||
                        (emEdicao ? "Erro ao salvar integrador" : "Erro ao cadastrar integrador"),
                    "error"
                )
            } else {
                alertStart(
                    emEdicao
                        ? "Integrador atualizado."
                        : "Integrador cadastrado. As lojas já podem conectar-se a ele.",
                    "success"
                )
                setEmEdicao(null)
                setFormulario(formularioVazio)
                carregar()
            }
        } finally {
            setSalvando(false)
        }
    }

    const handleAlternar = async (integrador: Integrador) => {
        if (
            integrador.ativo &&
            !window.confirm(
                `Desativar "${integrador.nome}" derruba as ${
                    integrador.quantidadeLojas ?? 0
                } loja(s) ligadas a ele já na próxima requisição. Continuar?`
            )
        ) {
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await alternarIntegrador(autorizacao, integrador.id)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao alterar", "error")
            else {
                alertStart(
                    res.ativo
                        ? "Integrador reativado."
                        : "Integrador desativado. As lojas dele perderam o acesso.",
                    res.ativo ? "success" : "warning"
                )
                carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integradores Open Delivery
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            {emEdicao
                                ? `Editando integrador: ${emEdicao.nome}`
                                : "Cadastrar novo integrador"}
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Nome"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.nome}
                                    onChange={handleChange("nome")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="E-mail de contato"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.contatoEmail}
                                    onChange={handleChange("contatoEmail")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Site"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.site}
                                    onChange={handleChange("site")}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Tipo do PDV"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.tipoPdv}
                                    onChange={handleChange("tipoPdv")}
                                    disabled={!!emEdicao}
                                    helperText={
                                        emEdicao
                                            ? "O tipo não muda depois de cadastrado."
                                            : TIPOS_PDV.find(t => t.valor === formulario.tipoPdv)
                                                  ?.descricao
                                    }
                                >
                                    {TIPOS_PDV.map(tipo => (
                                        <MenuItem
                                            key={tipo.valor}
                                            value={tipo.valor}
                                            disabled={!tipo.disponivel}
                                        >
                                            {tipo.rotulo}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="URL base do webhook do parceiro"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.webhookUrl}
                                    onChange={handleChange("webhookUrl")}
                                    helperText="Enviaremos POST {url}/v1/newEvent. Vazio = só polling."
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="AppId do parceiro (só logística)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.appIdParceiro}
                                    onChange={handleChange("appIdParceiro")}
                                    helperText="O que eles enviam no cabeçalho X-App-Id dos webhooks."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Observação"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={formulario.observacao}
                                    onChange={handleChange("observacao")}
                                />
                            </Grid>
                        </Grid>

                        <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 8 }}>
                            {emEdicao && (
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelarEdicao}
                                    disabled={salvando}
                                >
                                    Cancelar
                                </Button>
                            )}
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={emEdicao ? <SaveIcon /> : <AddIcon />}
                                onClick={handleSalvar}
                                disabled={salvando}
                            >
                                {salvando ? "Salvando..." : emEdicao ? "Salvar" : "Cadastrar"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Integradores cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {integradores.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                Nenhum integrador cadastrado.
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Entrega de eventos</TableCell>
                                        <TableCell align="center">Lojas</TableCell>
                                        <TableCell align="center">Situação</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {integradores.map(integrador => (
                                        <TableRow key={integrador.id}>
                                            <TableCell>
                                                {integrador.nome}
                                                {integrador.contatoEmail && (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="textSecondary"
                                                    >
                                                        {integrador.contatoEmail}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {integrador.logistica ? "Logística" : "PDV / hub"}
                                            </TableCell>
                                            <TableCell>
                                                {integrador.webhookUrl ? "Webhook" : "Só polling"}
                                            </TableCell>
                                            <TableCell align="center">
                                                {integrador.quantidadeLojas ?? 0}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={
                                                        integrador.ativo ? "ativo" : "desativado"
                                                    }
                                                    color={integrador.ativo ? "primary" : "default"}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleEditar(integrador)}
                                                    style={{ marginRight: 8 }}
                                                >
                                                    Editar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleAlternar(integrador)}
                                                >
                                                    {integrador.ativo ? "Desativar" : "Reativar"}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
